import { useCallback, useState } from "react";
import { useInView } from "react-intersection-observer";
import logger from "~/lib/logger";

interface UseInfiniteScrollProps<T> {
  initialData: T[];
  hasMore: boolean;
  onLoadMore: (params: {
    cursor?: string | number;
    offset?: number;
  }) => Promise<{
    data: T[];
    hasMore: boolean;
  }>;
  filterData?: (data: T[]) => T[];
  paginationType: "cursor" | "offset";
  getCursor?: (lastItem: T) => string | number;
  cursorParam?: string;
  limit?: number;
}

export function useInfiniteScroll<T>({
  initialData,
  hasMore: initialHasMore,
  onLoadMore,
  filterData,
  paginationType,
  getCursor,
}: UseInfiniteScrollProps<T>) {
  const [data, setData] = useState<T[]>(initialData);
  const [hasMore, setHasMore] = useState(initialHasMore);
  const [isLoading, setIsLoading] = useState(false);

  const loadFromCursor = useCallback(
    async (cursor: string | number) => {
      if (isLoading) return;

      try {
        setIsLoading(true);
        const { data: newData, hasMore: newHasMore } = await onLoadMore({
          cursor,
        });
        setData(filterData ? filterData(newData) : newData);
        setHasMore(newHasMore);
      } catch (error) {
        logger.error("Error loading more data", { error });
      } finally {
        setIsLoading(false);
      }
    },
    [onLoadMore, isLoading, filterData],
  );

  const loadMore = useCallback(async () => {
    if (!hasMore || isLoading) return;

    try {
      setIsLoading(true);
      const params: { cursor?: string | number; offset?: number } = {};

      if (paginationType === "cursor") {
        const lastItem = data[data.length - 1];
        if (!lastItem || !getCursor) return;
        const cursor = getCursor(lastItem);
        params.cursor = cursor;
      } else {
        params.offset = data.length;
      }

      const { data: newData, hasMore: newHasMore } = await onLoadMore(params);

      setData((prev) => {
        const combinedData = [...prev, ...newData];
        return filterData ? filterData(combinedData) : combinedData;
      });
      setHasMore(newHasMore);
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  }, [
    data,
    hasMore,
    isLoading,
    onLoadMore,
    filterData,
    paginationType,
    getCursor,
  ]);

  const { ref } = useInView({
    threshold: 0,
    rootMargin: "50px",
    onChange: (inView) => {
      if (inView) void loadMore();
    },
  });

  return {
    data,
    hasMore,
    loadFromCursor,
    isLoading,
    ref,
  };
}
